import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ShieldCheck, Sparkles } from 'lucide-react';
type PlanId = 'standard' | 'plus';
const PLANS: Record<PlanId, {
  id: PlanId;
  name: string;
  emoji: string;
  tagline: string;
  oldPrice: string;
  price: string;
  features: string[];
  url: string;
}> = {
  standard: {
    id: 'standard',
    name: 'ESTÁNDAR',
    emoji: '📘',
    tagline: '6 Tomos + 6 Bonos',
    oldPrice: '$34.990',
    price: '$19.990',
    features: ['6 Tomos Completos', '6 Bonos Estratégicos', 'Acceso de por vida', 'Actualizaciones gratuitas'],
    url: 'https://checkout.activadigital.store/cart/53243003437350:1'
  },
  plus: {
    id: 'plus',
    name: 'PACK PLUS™',
    emoji: '🏆',
    tagline: '6 Tomos + 6 Bonos + Consulta Organizada + Profesional Digital',
    oldPrice: '$43.990',
    price: '$27.990',
    features: ['Todo lo de Estándar', '+ Tu Consulta de Postparto Organizada', '+ Profesional Digital Redes'],
    url: 'https://checkout.activadigital.store/cart/53243005174054:1'
  }
};
export function PricingSelector() {
  const [selected, setSelected] = useState<PlanId>('plus');
  const activePlan = PLANS[selected];
  return <div className="max-w-4xl mx-auto" data-id="element-186">
      {/* Plan Selector — side-by-side on all screen sizes */}
      <div role="radiogroup" aria-label="Elegí tu kit" className="grid grid-cols-2 gap-2.5 md:gap-4 mb-4 md:mb-6" data-id="element-187">
        {(['standard', 'plus'] as PlanId[]).map(id => {
        const plan = PLANS[id];
        const isSelected = selected === id;
        const isPlus = id === 'plus';
        return <motion.button key={id} type="button" role="radio" aria-checked={isSelected} onClick={() => setSelected(id)} whileTap={{
          scale: 0.98
        }} className={`relative text-left rounded-2xl p-3 md:p-5 border-2 transition-all duration-300 flex flex-col h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-gold ${isSelected ? isPlus ? 'bg-navy border-gold shadow-xl shadow-navy/20' : 'bg-white border-navy shadow-lg' : 'bg-white/60 border-navy/10 hover:border-navy/30'}`} data-id="element-188">
              {/* MÁS ELEGIDO ribbon — only on Plus */}
              {isPlus && <motion.div animate={{
            scale: [1, 1.05, 1]
          }} transition={{
            repeat: Infinity,
            duration: 2.5
          }} className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-gold-400 to-gold-600 text-white px-2.5 py-0.5 rounded-full text-[9px] md:text-[11px] font-bold shadow-lg flex items-center gap-1 whitespace-nowrap" data-id="element-189">
                  <span data-id="element-190">🔥</span> MÁS ELEGIDO
                </motion.div>}

              {/* Radio indicator */}
              <div className="flex items-start justify-between mb-2 md:mb-3 mt-1" data-id="element-191">
                <div className={`flex items-center gap-1.5 md:gap-2 text-xs md:text-base font-bold ${isPlus && isSelected ? 'text-white' : 'text-navy'}`} data-id="element-192">
                  <span data-id="element-193">{plan.emoji}</span>
                  <span data-id="element-194">{plan.name}</span>
                </div>
                <div className={`shrink-0 w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? isPlus ? 'bg-gold border-gold' : 'bg-navy border-navy' : 'border-navy/30 bg-transparent'}`} data-id="element-195">
                  {isSelected && <Check className={`w-2.5 h-2.5 md:w-3 md:h-3 ${isPlus ? 'text-navy' : 'text-white'}`} strokeWidth={3} data-id="element-196" />}
                </div>
              </div>

              {/* Tagline */}
              <p className={`text-[10px] md:text-xs leading-tight mb-2 md:mb-3 ${isPlus && isSelected ? 'text-white/70' : 'text-navy/60'}`} data-id="element-197">
                {plan.tagline}
              </p>

              {/* Price */}
              <div className="mt-auto" data-id="element-198">
                <div className={`line-through text-[10px] md:text-xs font-medium ${isPlus && isSelected ? 'text-white/40' : 'text-navy/40'}`} data-id="element-199">
                  {plan.oldPrice}
                </div>
                <div className="flex items-baseline gap-1" data-id="element-200">
                  <span className={`text-xl md:text-3xl font-bold ${isPlus && isSelected ? 'text-white' : 'text-navy'}`} data-id="element-201">
                    {plan.price}
                  </span>
                  <span className={`text-[10px] md:text-xs font-medium ${isPlus && isSelected ? 'text-white/60' : 'text-navy/60'}`} data-id="element-202">
                    ARS
                  </span>
                </div>
              </div>
            </motion.button>;
      })}
      </div>

      {/* Features for selected plan */}
      <motion.div key={activePlan.id} initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25
    }} className="bg-white/60 border border-navy/10 rounded-2xl p-4 md:p-5 mb-4 md:mb-5" data-id="element-203">
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 md:gap-y-2.5" data-id="element-204">
          {activePlan.features.map((item, i) => <li key={i} className="flex items-start gap-2 text-navy/85 text-xs md:text-sm font-medium" data-id="element-205">
              <Check className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" data-id="element-206" />
              <span data-id="element-207">{item}</span>
            </li>)}
        </ul>
      </motion.div>

      {/* Unified CTA */}
      <motion.a href={activePlan.url} target="_blank" rel="noopener noreferrer" whileHover={{
      scale: 1.01
    }} whileTap={{
      scale: 0.98
    }} className="block w-full py-4 md:py-5 rounded-xl bg-terracotta hover:bg-terracotta-600 text-white font-bold text-base md:text-lg shadow-xl shadow-terracotta/30 transition-colors text-center" data-id="element-208">
        <span className="flex items-center justify-center gap-2" data-id="element-209">
          <Sparkles className="w-4 h-4 md:w-5 md:h-5" data-id="element-210" />
          <span data-id="element-211">
            QUIERO {activePlan.name} — {activePlan.price}
          </span>
        </span>
      </motion.a>

      {/* Trust line */}
      <div className="mt-4 md:mt-5 flex flex-wrap justify-center items-center gap-x-4 gap-y-1.5 text-[11px] md:text-xs text-navy/60 font-medium" data-id="element-212">
        <div className="flex items-center gap-1.5" data-id="element-213">
          <ShieldCheck className="w-3 h-3 md:w-3.5 md:h-3.5" data-id="element-214" /> Pago seguro
        </div>
        <div className="w-1 h-1 rounded-full bg-navy/20" data-id="element-215"></div>
        <div data-id="element-216">Acceso inmediato</div>
        <div className="w-1 h-1 rounded-full bg-navy/20" data-id="element-217"></div>
        <div data-id="element-218">Garantía 7 días</div>
      </div>
    </div>;
}